import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  Paper,
  TextField,
  IconButton,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Slider from "@mui/material/Slider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { useAccessToken } from "../../auth/useAccessToken";
import SectionSpinner from "../common/Loader/SectionSpinner";
import { formatValue } from "../../utils/currencyUtils";

import DetailsPageLowerLayer from "../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import {
  fetchAlertName,
  fetchAlertInventory,
} from "../../services/Alerts/AlertsService";
import { useLocation } from "react-router-dom";

/* ---------------- CONSTANTS ---------------- */

const ALL_EXCEPTIONS = "ALL";

const EXCEPTIONS_INVENTORY = [
  "LowStock",
  "Stockout",
  "Overstock",
  "BatchExpiring",
  "BatchExpired",
];

const EXCEPTIONS_SHIPMENT = [
  "Untracked",
  "PTA Unknown",
  "Late",
  "Delayed",
  "Early",
  "On Time",
];

const EXCEPTIONS_ORDERS = ["Past Due", "Late", "Unconfirmed"];

const ALERT_TITLE_MAP = {
  inventory: "Inventory Alerts",
  "inbound-shipments": "Inbound Shipment Alerts",
  "outbound-shipments": "Outbound Shipment Alerts",
  "purchase-orders": "Purchase Order Alerts",
  "sales-orders": "Sales Order Alerts",
};

const EXCEPTION_COUNT_MAP_INVENTORY = {
  LowStock: "lowStockCount",
  Stockout: "stockOutCount",
  Overstock: "overStockCount",
  BatchExpiring: "lotExpiringCount",
  BatchExpired: "lotExpiredCount",
};

const EXCEPTION_COUNT_MAP_SHIPMENT = {
  "Untracked": "untrackedCount",
  "PTA Unknown": "ptaUnknownCount",
  "Late": "lateCount",
  "Delayed": "delayedCount",
  "Early": "earlyCount",
  "On Time": "onTimeCount",
};

const EXCEPTION_COUNT_MAP_ORDERS = {
  "Past Due": "pastDueCount",
  "Late": "orderLateCount",
  "Unconfirmed": "unconfirmedCount",
};

const linkCellStyle = {
  color: "#1976d2",
  cursor: "pointer",
  textDecoration: "underline",
  fontSize: "inherit",
  fontFamily: "inherit",
  lineHeight: "inherit",
  display: "flex",
  alignItems: "center",
};


/* ---------------- HELPERS ---------------- */

const getCategoryType = (category) => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("inbound") || cat.includes("outbound")) return "shipment";
  if (cat.includes("purchase") || cat.includes("sales")) return "order";
  return "inventory";
};

const normalizeInventoryResponse = (apiData = {}) => [
  { exceptionType: "LowStock", records: apiData.lowStockExceptionList || [] },
  { exceptionType: "Stockout", records: apiData.stockOutExceptionList || [] },
  { exceptionType: "Overstock", records: apiData.overstockExceptionList || [] },
  { exceptionType: "BatchExpiring", records: apiData.lotExpiringExceptionList || [] },
  { exceptionType: "BatchExpired", records: apiData.lotExpiredExceptionList || [] },
];

const normalizeShipmentResponse = (apiData = {}) => [
  { exceptionType: "Untracked", records: apiData.untrackedExceptionList || [] },
  { exceptionType: "PTA Unknown", records: apiData.ptaUnknownExceptionList || [] },
  { exceptionType: "Late", records: apiData.lateExceptionList || [] },
  { exceptionType: "Delayed", records: apiData.delayedExceptionList || [] },
  { exceptionType: "Early", records: apiData.earlyExceptionList || [] },
  { exceptionType: "On Time", records: apiData.onTimeExceptionList || [] },
];

const normalizeOrderResponse = (apiData = {}) => [
  { exceptionType: "Past Due", records: apiData.pastDueExceptionList || [] },
  { exceptionType: "Late", records: apiData.orderLateExceptionList || [] },
  { exceptionType: "Unconfirmed", records: apiData.unconfirmedExceptionList || [] },
];

const formatDate = (ts) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

/* ---------------- COMPONENT ---------------- */

const AlertDetails = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { email: reduxEmail } = useSelector((state) => state.user || {});
  const accessToken = useAccessToken();

  const [decodedToken, setDecodedToken] = useState(null);
  const [alertNames, setAlertNames] = useState([]);
  const [data, setData] = useState([]);
  const [exceptionCounts, setExceptionCounts] = useState({});
  const [alertDescription, setAlertDescription] = useState([]);
  const [loading, setLoading] = useState(false);


  const [selectedException, setSelectedException] = useState(ALL_EXCEPTIONS);
  const [selectedAlerts, setSelectedAlerts] = useState([]);

  const pathSegments = location.pathname.split("/");
  let category = pathSegments[2] || "";

  const categoryType = getCategoryType(category);
  const pageTitle = ALERT_TITLE_MAP[category] || "Alerts";

  // Determine exceptions based on category
  const EXCEPTIONS =
    categoryType === "shipment"
      ? [ALL_EXCEPTIONS, ...EXCEPTIONS_SHIPMENT]
      : categoryType === "order"
        ? [ALL_EXCEPTIONS, ...EXCEPTIONS_ORDERS]
        : [ALL_EXCEPTIONS, ...EXCEPTIONS_INVENTORY];

  const EXCEPTION_COUNT_MAP =
    categoryType === "shipment"
      ? EXCEPTION_COUNT_MAP_SHIPMENT
      : categoryType === "order"
        ? EXCEPTION_COUNT_MAP_ORDERS
        : EXCEPTION_COUNT_MAP_INVENTORY;

  // Set default exception on mount or category change
  // useEffect(() => {
  //   if (EXCEPTIONS.length > 0) {
  //     setSelectedException(ALL_EXCEPTIONS);
  //   }
  // }, [categoryType]);

  const getExceptionCount = (type) => {
    if (type === ALL_EXCEPTIONS) {
      return EXCEPTIONS
        .filter((t) => t !== ALL_EXCEPTIONS)
        .reduce(
          (sum, t) =>
            sum + (exceptionCounts?.[EXCEPTION_COUNT_MAP[t]] || 0),
          0
        );
    }

    return exceptionCounts?.[EXCEPTION_COUNT_MAP[type]] ?? 0;
  };


  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).getTime();
  const endPlus365 = startOfToday + 364 * 24 * 60 * 60 * 1000;

  const [sliderRange, setSliderRange] = useState([startOfToday, endPlus365]);
  const [dateRange, setDateRange] = useState([null, null]);

  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");

  // Column definitions per category type
  const COMMON_COLUMNS_SHIPMENT = [
    { field: "alertName", headerName: "Alert Name", flex: 1.3 },
    { field: "shipmentNumber", headerName: "Shipment No", flex: 1 },
    { field: "headerState", headerName: "Header State", flex: 1 },
    { field: "eta", headerName: "ETA", flex: 1 },
    { field: "pta", headerName: "PTA", flex: 1 },
    { field: "maxDelayInDays", headerName: "Difference", flex: 1 },
    { field: "supplier", headerName: "Supplier", flex: 1 },
    { field: "customer", headerName: "Customer", flex: 1 },
    { field: "carrier", headerName: "Carrier", flex: 1 },
    // { field: "mrpController", headerName: "Mrp Controller", flex: 1 },
  ];

  const COMMON_COLUMNS_ORDER = [
    { field: "alertName", headerName: "Alert Name", flex: 1.3 },
    { field: "orderNo", headerName: "Order No", flex: 1 },
    { field: "orderType", headerName: "Order Type", flex: 1 },
    { field: "headerState", headerName: "Header State", flex: 1 },
    { field: "confirmedDeliveryDays", headerName: " Original Confirmed Delivery Date", flex: 1 },
    { field: "requestedDate", headerName: "Requested Date", flex: 1 },
    { field: "plannedShipDate", headerName: "Planned Ship Date", flex: 1 },
    { field: "actualShipDate", headerName: "Container ship date", flex: 1 },
    { field: "confirmedDateBySupplier", headerName: "Confirmed Date By Supplier", flex: 1 },
    { field: "creationDate", headerName: "Creation Date", flex: 1 },
    { field: "supplier", headerName: "Supplier", flex: 1 },
    { field: "customer", headerName: "Customer", flex: 1 },
    { field: "carrier", headerName: "Carrier", flex: 1 },
    // { field: "mrpController", headerName: "Mrp Controller", flex: 1 },
  ];

  const COMMON_COLUMNS_INVENTORY = [
    { field: "alertName", headerName: "Alert Name", flex: 1.3 },
    { field: "material", headerName: "Material", flex: 1 },
    { field: "materialsDescription", headerName: "Material Description", flex: 1 },
    { field: "plant", headerName: "Plant", flex: 1 },
    { field: "mrpController", headerName: "MRP Controller", flex: 1 },
  ]

  const SALES_ORDER_HIDDEN_FIELDS = [
    "orderType",
    "confirmedDateBySupplier",
    "mrpController",
  ];


  const baseColumns = useMemo(() => {
    if (categoryType === "shipment") {
      return COMMON_COLUMNS_SHIPMENT;
    } else if (categoryType === "order") {
      if (category === "sales-orders") {
        return COMMON_COLUMNS_ORDER.filter(
          (col) => !SALES_ORDER_HIDDEN_FIELDS.includes(col.field)
        );
      }
      return COMMON_COLUMNS_ORDER;
    } else {
      // Inventory
      if (
        selectedException === ALL_EXCEPTIONS ||
        selectedException === "LowStock" ||
        selectedException === "Stockout" ||
        selectedException === "Overstock"
      ) {
        return [
          ...COMMON_COLUMNS_INVENTORY,
          { field: "occurenceWeek", headerName: "Occurrence Week", flex: 1 },
          { field: "weekQuantity", headerName: "Quantity Deviation", flex: 1 },
          { field: "deviationPercentage", headerName: "Deviation %", flex: 1 },
        ];
      }

      if (
        selectedException === "BatchExpiring" ||
        selectedException === "BatchExpired"
      ) {
        return [
          ...COMMON_COLUMNS_INVENTORY,
          { field: "quantity", headerName: "Batch Quantity", flex: 1 },
          { field: "daystoExpire", headerName: "Days To Expire", flex: 1 },
          { field: "batchId", headerName: "Batch", flex: 1 },
        ];
      }

      return COMMON_COLUMNS_INVENTORY;
    }
  }, [categoryType, selectedException]);

  /* ---------------- AUTH ---------------- */

  useEffect(() => {
    if (!accessToken) return;
    try {
      setDecodedToken(jwtDecode(accessToken));
    } catch {
      setDecodedToken(null);
    }
  }, [accessToken]);

  const userEmail =
    decodedToken?.unique_name || decodedToken?.email || reduxEmail || "";

  /* ---------------- FETCH ALERT NAMES ---------------- */

  useEffect(() => {
    let isMounted = true;
    setSelectedAlerts((prev) => (prev.length ? [] : prev));

    fetchAlertName(formatCategoryForPayload(category))
      .then(({ alertNames, alertDetails }) => {
        if (!isMounted) return;

        setAlertNames(Array.isArray(alertNames) ? alertNames : []);
        setAlertDescription(
          Array.isArray(alertDetails) ? alertDetails : []
        );
      })
      .catch(console.error);

    return () => {
      isMounted = false;
    };
  }, [category]);

  /* ---------------- FETCH ALERT DATA ---------------- */

  const formatCategoryForPayload = (category) => {
    const categoryMap = {
      "inbound-shipments": "inboundShipments",
      "outbound-shipments": "outboundShipments",
      "purchase-orders": "purchaseOrders",
      "sales-orders": "salesOrders",
      "inventory": "inventory",
    };
    return categoryMap[category] || category;
  };

  useEffect(() => {
    if (!userEmail) return;
    if (!selectedException) return;

    const [start, end] = dateRange;
    const isPartialDateSelected = (start && !end) || (!start && end);

    if (category === "inventory" && isPartialDateSelected) {
      return;
    }
    setLoading(true);

    const payload = {
      userEmail,
      category: formatCategoryForPayload(category),
      exceptionType:
        selectedException === ALL_EXCEPTIONS ? null : selectedException,
      alertName:
        selectedAlerts.length > 0 ? selectedAlerts : null,
      periodStartDate: dateRange[0] ? formatDate(dateRange[0]) : "",
      periodEndDate: dateRange[1] ? formatDate(dateRange[1]) : "",
    };

    fetchAlertInventory(payload)
      .then((res) => {

        if (categoryType === "shipment") {
          setExceptionCounts(res)
          setData(normalizeShipmentResponse(res));
        } else if (categoryType === "order") {
          setExceptionCounts(res)
          setData(normalizeOrderResponse(res));
        } else {
          setExceptionCounts(res)
          setData(normalizeInventoryResponse(res));
        }
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [selectedException, selectedAlerts, dateRange, categoryType, category, userEmail]);

  const handleStartDateChange = (e) => {
    setDateRange(([_, end]) => [
      e.target.value ? new Date(e.target.value).getTime() : null,
      end,
    ]);
  };

  const handleEndDateChange = (e) => {
    setDateRange(([start]) => [
      start,
      e.target.value ? new Date(e.target.value).getTime() : null,
    ]);
  };

  const getAlertDiscription = (alertName) => {

    const alertObj = alertDescription?.find(
      (item) => item?.alertName == alertName
    );

    const description =
      alertObj?.description || "No description available.";

    setSelectedSummary(description);
    setSummaryDialogOpen(true);
  };

  const extractFirstValue = (value = "") =>
    value.trim().split(/[-\s]/)[0];

  const handleInventoryDetailClick = (material, plant) => () => {
    const materialRaw = extractFirstValue(material);
    const plantRaw = extractFirstValue(plant);

    navigate(
      `/core/inventory/node/inventory/inventory-detail/${materialRaw}/${plantRaw}`
    );
  };

  const handleShipmentClick = (shipmentNo) => () => {
    if (!shipmentNo) return;

    if (category === "inbound-shipments") {
      navigate(`/core/supply/shipment/shipment-detail/${shipmentNo}`);
    } else if (category === "outbound-shipments") {
      navigate(`/core/demand/shipment/shipment-detail/${shipmentNo}`);
    }
  };

  const handleOrderClick = (orderNo) => () => {
    if (!orderNo) return;

    if (category === "purchase-orders") {
      navigate(`/core/supply/order/order-detail/stockTransferPO/${orderNo}`);
    } else if (category === "sales-orders") {
      navigate(`/core/demand/order/order-detail/${orderNo}`);
    }
  };

  const rows = useMemo(() => {
    if (selectedException === ALL_EXCEPTIONS) {
      return data.flatMap((group) =>
        (group.records || []).map((row) => ({
          ...row,
          exceptionType: group.exceptionType,
        }))
      );
    }

    return (
      data.find((d) => d.exceptionType === selectedException)?.records || []
    );
  }, [data, selectedException]);


  const columns = useMemo(() => {
    let cols = [...baseColumns];

    if (selectedException === ALL_EXCEPTIONS) {
      cols = [
        {
          field: "exceptionType",
          headerName: "Exception Type",
          flex: 1,
        },
        ...cols,
      ];
    }

    return cols.map((col) => {


      if (["weekQuantity", "quantity"].includes(col.field)) {
        return {
          ...col,
          cellRenderer: (params) =>
            params.value !== null && params.value !== undefined
              ? formatValue(params.value)
              : "",
        };
      }

      if (col.field === "alertName") {
        return {
          ...col,
          cellRenderer: (params) => (
            <span style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "inherit",
            }}>
              {params.value}
              <IconButton
                size="small"
                onClick={() => getAlertDiscription(params.value)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          ),
        };
      }

      if (col.field === "material" && categoryType === "inventory") {
        return {
          ...col,
          cellRenderer: (params) => {
            const material = params.data?.material;
            const plant = params.data?.plant;

            return (
              <Typography
                style={linkCellStyle}
                onClick={handleInventoryDetailClick(material, plant)}
              >
                {material}
              </Typography>
            );
          },
        };
      }

      if (
        col.field === "shipmentNumber" &&
        (category === "inbound-shipments" ||
          category === "outbound-shipments")
      ) {
        return {
          ...col,
          cellRenderer: (params) => (
            <Typography
              style={linkCellStyle}
              onClick={handleShipmentClick(params.value)}
            >
              {params.value}
            </Typography>
          ),
        };
      }

      if (
        col.field === "orderNo" &&
        (category === "purchase-orders" ||
          category === "sales-orders")
      ) {
        return {
          ...col,
          cellRenderer: (params) => (
            <Typography
              style={linkCellStyle}
              onClick={handleOrderClick(params.value)}
            >
              {params.value}
            </Typography>
          ),
        };
      }

      return col;
    });
  }, [
    baseColumns,
    selectedException,
    categoryType,
    category,
    alertDescription,
  ]);


  const isAllSelected =
    alertNames.length > 0 && selectedAlerts.length === alertNames.length;

  const handleAlertChange = (event) => {
    const value = event.target.value;

    if (value.includes("ALL")) {
      setSelectedAlerts(isAllSelected ? [] : alertNames);
    } else {
      setSelectedAlerts(value);
    }
  };

  const handleExceptionClick = (type) => {
    setSelectedException(type);
  };


  return (
    <Box>
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 0.5,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {pageTitle}
        </Typography>
      </Box>
      <Paper sx={{ m: 1, px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* ALERT */}
          <Box sx={{ width: 220 }}>
            <Typography variant="overline">Alert Names</Typography>
            <Select
              multiple
              fullWidth
              size="small"
              value={selectedAlerts}
              onChange={(e) => {
                if (loading) return;
                handleAlertChange(e);
              }}
              disabled={loading}
              renderValue={(v) => (v.length ? v.join(", ") : "All")}
            >
              {/* ✅ SELECT ALL */}
              <MenuItem value="ALL" disabled={loading}>
                <Checkbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={
                    selectedAlerts.length > 0 &&
                    selectedAlerts.length < alertNames.length
                  }
                />
                <ListItemText primary="Select All" />
              </MenuItem>

              {alertNames.map((name) => {
                const isSelected = selectedAlerts.includes(name);

                return (
                  <MenuItem
                    key={name}
                    value={name}
                    disabled={loading}   // ✅ disable ALL
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                    />
                    <ListItemText primary={name} />
                  </MenuItem>
                );
              })}


            </Select>
          </Box>
          {category == 'inventory' && (
            <Box sx={{ width: 280 }}>
              <Typography variant="overline">Time Horizon override</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={dateRange[0] ? formatDate(dateRange[0]) : undefined}
                  onChange={handleStartDateChange}
                />
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  disabled={!dateRange[0]}
                  value={dateRange[1] ? formatDate(dateRange[1]) : undefined}
                  onChange={handleEndDateChange}
                />
              </Box>
            </Box>
          )}
          {/* 🔹 VERTICAL SEPARATOR */}
          <Box
            sx={{
              width: "1px",
              height: 48,
              bgcolor: "#E5E7EB",
              mx: 1,
            }}
          />

          {/* EXCEPTIONS */}
          <Box>
            <Typography variant="overline">Exception Type</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>

              {EXCEPTIONS.map((type) => (
                <Paper
                  key={type}
                  onClick={() => handleExceptionClick(type)}
                  sx={{
                    minWidth: 90,
                    px: 1,
                    py: 0.5,
                    textAlign: "center",
                    cursor: "pointer",
                    border:
                      selectedException === type
                        ? "2px solid #D32F2F"
                        : "1px solid #E5E7EB",
                  }}
                >
                  <Typography fontSize={12} fontWeight={600}>
                    {type.replace(/([A-Z])/g, " $1")}
                  </Typography>
                  <Typography fontSize={11}>
                    {getExceptionCount(type)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ================= TABLE ================= */}
      {loading ? (
        <SectionSpinner />
      ) : (
        <DetailsPageLowerLayer
          hideTabs
          tabs={[{ rows, columns, loading: false }]}
        />
      )}

      {/* ================= SUMMARY DIALOG ================= */}
      <Dialog open={summaryDialogOpen} onClose={() => setSummaryDialogOpen(false)}>
        <DialogTitle>Alert Details</DialogTitle>
        <DialogContent>
          <Typography>{selectedSummary}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertDetails;